/**
 * ChecklistTypeFilter — filtro pesquisável compartilhado para tipos de checklist.
 *
 * Reutiliza o padrão Popover + Command aplicado nos filtros rápidos do CRM,
 * mantendo uma única consulta cacheada de tipos para Lista e Kanban de Vistorias.
 *
 * @version 1.0.0
 * RELEASE: 2026-09-26 v1.0.0 - Criação do filtro pesquisável compartilhado.
 */
import { useState } from 'react';
import { Check, ChevronDown, Shapes } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useChecklistTypes } from '@/hooks/useChecklistTypes';
import { cn } from '@/lib/utils';

interface ChecklistTypeFilterProps {
  /** UUID selecionado ou "all" para não filtrar. */
  value: string;
  /** Informa a seleção ao estado compartilhado da página. */
  onChange: (value: string) => void;
  /** Permite aplicar o tamanho compacto do Kanban ou o padrão da listagem. */
  className?: string;
}

/** Seletor de tipo com pesquisa local e lista rolável. */
export function ChecklistTypeFilter({ value, onChange, className }: ChecklistTypeFilterProps) {
  const [open, setOpen] = useState(false);
  const { data: checklistTypes = [], isLoading } = useChecklistTypes(false);
  const selectedType = checklistTypes.find((type) => type.id === value);

  /** Seleciona o tipo e fecha o menu para manter o filtro rápido. */
  const handleSelect = (nextValue: string) => {
    onChange(nextValue);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          role="combobox"
          aria-expanded={open}
          aria-label="Filtrar por tipo de checklist"
          className={cn(
            'h-8 min-w-0 justify-between gap-1.5 px-2.5 font-normal',
            value !== 'all' && 'border-primary bg-primary/5',
            className,
          )}
        >
          <span className="flex min-w-0 items-center gap-1.5">
            <Shapes className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span className="truncate">{isLoading ? 'Carregando...' : selectedType?.name ?? 'Tipo de Checklist'}</span>
          </span>
          <ChevronDown className="h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Buscar tipo..." maxLength={100} />
          <CommandList>
            <CommandEmpty>Nenhum tipo encontrado.</CommandEmpty>
            <CommandGroup>
              <CommandItem value="__all__" onSelect={() => handleSelect('all')} className="cursor-pointer">
                <span className={cn(
                  'mr-2 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
                  value === 'all' ? 'border-primary bg-primary' : 'border-muted',
                )}>
                  {value === 'all' && <Check className="h-2.5 w-2.5 text-primary-foreground" />}
                </span>
                <Shapes className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
                <span>Todos os tipos</span>
              </CommandItem>
            </CommandGroup>
            <CommandSeparator />
            <CommandGroup heading="Tipos de Checklist">
              {checklistTypes.map((type) => (
                <CommandItem
                  key={type.id}
                  value={type.name}
                  onSelect={() => handleSelect(type.id)}
                  className="cursor-pointer"
                >
                  <span className={cn(
                    'mr-2 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
                    value === type.id ? 'border-primary bg-primary' : 'border-muted',
                  )}>
                    {value === type.id && <Check className="h-2.5 w-2.5 text-primary-foreground" />}
                  </span>
                  <span className="truncate">{type.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}